import styled, { createGlobalStyle, css } from "styled-components";
import React from "react";

interface Props {
  className?: string;
  children?: React.ReactNode;
}

/** The token declarations only (no box-sizing/color) — shared between the app-scoped `Styles` div
 *  below and `RootTokens`, which puts the same values on `:root` so ancestor-level global styles
 *  (e.g. `spotter`'s `SpotterThemeProvider`, mounted as a `:root`-targeting `createGlobalStyle`
 *  that sits *above* this component's own div in the DOM) can read them via `var(...)` too —
 *  custom properties only inherit downward, so a `:root` rule can't see a variable defined solely
 *  on a descendant div. Kept in one place so the two can never drift apart. */
const tokens = css`
  /* Neutral ramp set to Spotter's own slate family using its EXACT hex values, assigned by the
   * contextual role each stop actually plays (not just a hue/sat approximation — see prior
   * revision's comment for that pass). Spotter's own tiers, darkest to lightest, are nflPaper <
   * nflSlate < nflSurface < nflRaised < nflLine < nflLine2 < nflInkFaint < nflInkSoft < nflInk;
   * mapped onto this ramp in that same order so every existing "N steps darker/lighter"
   * relationship still holds: dark060 = page canvas (nflPaper), dark090 = header/chrome bar
   * (nflSlate — Spotter's header sits one tier darker than its cards, not the same tone; this
   * resolves that as its own token instead of doubling up dark100), dark100 = card/row surface
   * (nflSurface), dark200 = raised/hover chrome (nflRaised), dark300 = subtle divider (nflLine),
   * dark400 = stronger divider/input border (nflLine2), dark700 = faint/placeholder text
   * (nflInkFaint). The remaining in-between stops (110/500/600) have no direct Spotter
   * equivalent — interpolated in the same hue/sat family to keep the ramp smooth. */
  --dark060: #121519; /* nflPaper */
  --dark090: #14181d; /* nflSlate */
  --dark100: #191d22; /* nflSurface */
  --dark110: #1c2126;
  --dark200: #1f242a; /* nflRaised */
  --dark300: #272c31; /* nflLine */
  --dark400: #3a4046; /* nflLine2 */
  --dark500: #4d545c;
  --dark500a: rgba(77, 84, 92, 0.5);
  --dark600: #61686f;
  --dark700: #79818a; /* nflInkFaint */
  /* Matches Spotter's own --inputBackground: var(--spotterRaised); --inputBorder: var(--spotterLine2)
   * override — now the global default, so that override is a harmless no-op on Spotter's routes. */
  --inputBorder: var(--dark400);
  --inputBackground: var(--dark200);
  --overlay: #afafaf4d;
  --gray050: #838b93;
  --gray100: #929aa1;
  --gray200: #a7aeb4; /* nflInkSoft */
  --gray300: #c2c7cb;
  --gray400: #e7e9e9; /* nflInk */
  --gray500: hsl(0, 0%, 100%);
  /* Shared "strong" text tone for a muted breadcrumb's emphasized segment (e.g. the customer/org
   * name in spotter's and ab-vault's header crumbs) — promoted out of
   * SpotterThemeProvider's nflHeaderCrumbStrong (which aliases here) so ab-vault's AbHeader can use the
   * same value without depending on a route-scoped NFL-only token. */
  --headerCrumbStrong: #cdd4da;
  /* Shared "muted" tone for the rest of that same breadcrumb (the non-emphasized text around the
   * strong segment above) — promoted out of SpotterThemeProvider's nflHeaderMuted the same way. */
  --headerCrumbMuted: #939ca4;
  --blueBackground: hsl(230, 100%, 76%, 0.3);
  --blueBorderOverlay: hsl(230, 100%, 80%, 0.17);
  --blueBorder: hsl(230, 100%, 80%, 0.5);
  /* Dedicated, dimmer token for the Input/Select focus ring specifically — Spotter's own focus
   * treatment (its \`--blueBackground\` override resolves to \`--spotterAccentSoft\`, a low-saturation,
   * low-alpha solid) reads as a faint wash, not a bright halo. \`--blueBackground\` itself stays
   * untouched since Table's active-row fill and Button's selected state need more contrast than a
   * focus ring does. */
  --blueFocusRing: hsl(230, 55%, 60%, 0.18);
  --blue030: hsl(230, 42%, 37%);
  --blue060: hsl(230, 27%, 49%);
  --blue090: hsl(230, 42%, 57%);
  --blue100: hsl(230, 42%, 62%);
  --blue200: hsl(230, 42%, 67%);
  --blue300: hsl(230, 42%, 72%);
  --blue400: hsl(229, 42%, 77%);
  --blue400a: hsla(229, 42%, 77%, 0.2);
  --blue500: hsl(229, 42%, 82%);
  /* Green/red re-hued off the richer tones introduced for the AB Vault/Spotter customer-portal
   * work (nflOk #6fbf95 ≈ hsl(149, 38%, 59%), nflDanger #e2604a ≈ hsl(9, 72%, 59%)) — same
   * lightness-step structure as the untouched blue ramp above, just a warmer coral-red instead of
   * the old crimson/pink and a teal-leaning green instead of the old pure green, so both read as
   * part of the same family as the accent teal/amber those interfaces use. Orange is left as-is —
   * it was already close to AB's own warning tone (abWarn #ffb11b ≈ hsl(40, 100%, 55%)) — and blue
   * (the app's primary/accent hue) is deliberately untouched. The *Background/*Border tokens below
   * are Spotter's own nflOkSoft/nflOkLine/nflDangerSoft/nflDangerBorder hex verbatim — solid, not
   * alpha-blended, matching how Spotter itself uses them as flat fills tuned to sit on --dark100. */
  --greenBackground: #1b2a22; /* nflOkSoft */
  --greenBorderOverlay: #2e4536; /* nflOkLine */
  --greenBorder: #2e4536; /* nflOkLine */
  --green100: hsl(150, 38%, 42%);
  --green200: hsl(150, 38%, 48%);
  --green300: hsl(150, 38%, 54%);
  --green300a: hsla(150, 38%, 54%, 0.2);
  --green400: hsl(150, 38%, 60%);
  --green500: hsl(150, 38%, 66%);
  --orangeBackground: hsl(36, 92%, 53%, 0.3);
  --orangeBorderOverlay: hsl(36, 100%, 76%, 0.17);
  --orangeBorder: hsl(36, 100%, 76%, 0.5);
  /* Dedicated, dimmer background for small pill/chip treatments (e.g. the "N unsaved changes"
   * trigger) — orangeBackground itself stays untouched since other consumers (job-status
   * badges, inline warning banners) are tuned to that brightness already. Same "add a dimmer
   * sibling token instead of changing the shared one" pattern as blueFocusRing. */
  --orangeChipBackground: hsl(36, 92%, 53%, 0.14);
  --orange100: hsl(36, 100%, 43%);
  --orange200: hsl(36, 100%, 48%);
  --orange300: hsl(36, 100%, 53%);
  --orange400: hsl(36, 100%, 58%);
  --orange400a: hsla(36, 100%, 58%, 0.2);
  --orange500: hsl(36, 100%, 63%);
  --redBackground: #3a221c; /* nflDangerSoft */
  --redBorderOverlay: #4a2c24; /* nflDangerBorder */
  --redBorder: #4a2c24; /* nflDangerBorder */
  --red060: hsl(9, 65%, 34%);
  --red100: hsl(9, 65%, 40%);
  --red200: hsl(9, 65%, 46%);
  --red300: hsl(9, 65%, 55%);
  --red400: hsl(9, 65%, 63%);
  --red400a: hsla(9, 65%, 63%, 0.2);
  --red500: hsl(9, 65%, 70%);
  --customAction: #ff65d6;
  --webhook: #65d8ff;
  --variable: #07dab0;
  --schedule: #e9bf17;
  --metadataView: #ba7fee;
  --listInput: #ffa065;
`;

const RootTokens = createGlobalStyle`
  :root {
    ${tokens}
  }
`;

const Styles = styled.div`
  box-sizing: border-box;

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ${tokens}

  color: var(--gray400);
`;

const ColorProvider = (props: Props) => {
  const { className, children } = props;
  return (
    <>
      <RootTokens />
      <Styles className={className}>{children}</Styles>
    </>
  );
};

export default ColorProvider;
